import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BadgeCheck, BellRing, Coins } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLeadBalance } from '@/lib/creditsBus';
import { canBuyLeads } from '@/lib/roles';
import { membershipCopy } from '@/lib/membershipValue';
import { track } from '@/lib/tracking';

/**
 * ABOVE-THE-FOLD MEMBERSHIP LINE (hero, phones + desktop).
 *
 * The hero sub-headline is hidden on phones, so this strip is the only
 * membership message a mobile visitor sees before scrolling. Every figure
 * comes from membershipCopy() -> src/lib/membershipValue.ts -> constants.ts.
 *
 * Signed-in contractors/subs who ALREADY have an active plan are not pitched
 * a membership they own: they see their live credit balance instead.
 */
const MemberValueStrip: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { path, lang } = useI18n();
  const { profile } = useAuth();
  const eligible = !!profile && canBuyLeads(profile.user_type);
  const { balance } = useLeadBalance(eligible ? profile?.id : undefined);
  const copy = membershipCopy(lang);

  const isMember = eligible && balance.hasSubscription && balance.status === 'active';

  if (isMember) {
    return (
      <Link
        to={path('/jobs')}
        onClick={() => track('cta_click', { action: 'home_member_strip_active' })}
        className={`inline-flex w-fit flex-wrap items-center gap-2 rounded-full bg-emerald-500/15 px-4 py-2 text-sm font-bold text-emerald-100 ring-1 ring-emerald-400/40 backdrop-blur transition-colors hover:bg-emerald-500/25 ${className}`}
      >
        <Coins className="h-4 w-4 text-emerald-300" aria-hidden="true" />
        {copy.memberActive(balance.spendable, balance.planCode)}
        <span className="inline-flex items-center gap-1 text-white">
          {copy.memberActiveCta} <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </span>
      </Link>
    );
  }

  return (
    <Link
      to={path('/pricing')}
      onClick={() => track('cta_click', { action: 'home_member_strip' })}
      data-member-strip
      className={`group inline-flex w-fit flex-wrap items-center gap-x-2 gap-y-1 rounded-2xl bg-brand-orange/20 px-4 py-2.5 text-sm font-semibold leading-snug text-white ring-1 ring-brand-orange/60 backdrop-blur transition-colors hover:bg-brand-orange/30 sm:rounded-full ${className}`}
    >
      <BadgeCheck className="h-4 w-4 shrink-0 text-orange-300" aria-hidden="true" />
      <span>{copy.strip}</span>
      {/* Member-first job alerts — live since job-alerts-digest v3.1 (2026-10-14). */}
      <span className="inline-flex items-center gap-1 font-bold text-emerald-200">
        <BellRing className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> {copy.memberFirst}.
      </span>
      <span className="inline-flex items-center gap-1 font-extrabold text-orange-200 group-hover:text-white">
        {copy.stripCta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
      </span>
    </Link>
  );
};

export default MemberValueStrip;
